import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format, differenceInCalendarDays } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import {
  MapPin, Users, Bed, Bath, Star, Heart, CalendarIcon,
  Wifi, Car, Waves, Coffee, ArrowLeft,
  ChevronLeft, ChevronRight, X, Images,
} from "lucide-react";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { whatsappUrl } from "@/lib/brand";

function amenityIcon(a) {
  const l = a.toLowerCase();
  if (l.includes("wifi")) return <Wifi className="h-4 w-4" />;
  if (l.includes("parking")) return <Car className="h-4 w-4" />;
  if (l.includes("pool") || l.includes("beach")) return <Waves className="h-4 w-4" />;
  if (l.includes("breakfast") || l.includes("coffee")) return <Coffee className="h-4 w-4" />;
  return <span className="h-1.5 w-1.5 rounded-full bg-[hsl(10_51%_55%)]" />;
}

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [wishlisted, setWishlisted] = useState(false);

  const [checkIn, setCheckIn] = useState();
  const [checkOut, setCheckOut] = useState();
  const [guests, setGuests] = useState(2);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setProperty(r.data)).catch(() => navigate("/search"));
    api.get(`/properties/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
    api.get("/wishlist").then((r) => setWishlisted((r.data.property_ids || []).includes(id))).catch(() => {});
  }, [id, navigate]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxOpen(false);
      if (e.key === "ArrowRight") setLightboxIndex((i) => (property?.images?.length ? (i + 1) % property.images.length : i));
      if (e.key === "ArrowLeft") setLightboxIndex((i) => (property?.images?.length ? (i - 1 + property.images.length) % property.images.length : i));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen, property]);

  if (!property) {
    return <div className="text-center py-32 text-[hsl(100_3%_30%)]">Loading…</div>;
  }

  const nights = checkIn && checkOut ? Math.max(0, differenceInCalendarDays(checkOut, checkIn)) : 0;
  const subtotal = nights * property.price_per_night;
  const taxes = Math.round(subtotal * 0.12);
  const total = subtotal + taxes;

  const toggleWishlist = async () => {
    if (!user || user === false) return toast.error("Please sign in to save this stay");
    try {
      const { data } = await api.post("/wishlist/toggle", { property_id: id });
      setWishlisted(data.in_wishlist);
    } catch { toast.error("Wishlist error"); }
  };

  const onReserve = () => {
    if (!checkIn || !checkOut) return toast.error("Please pick your dates");
    if (nights <= 0) return toast.error("Check-out must be after check-in");
    if (guests > property.max_guests) return toast.error(`Max ${property.max_guests} guests`);
    const params = new URLSearchParams({
      property_id: id,
      check_in: format(checkIn, "yyyy-MM-dd"),
      check_out: format(checkOut, "yyyy-MM-dd"),
      guests: String(guests),
    });
    navigate(`/checkout?${params.toString()}`);
  };

  const submitReview = async () => {
    if (!user || user === false) return toast.error("Please sign in to review");
    if (!reviewComment.trim()) return toast.error("Please write a comment");
    try {
      await api.post("/reviews", { property_id: id, rating: reviewRating, comment: reviewComment });
      const r = await api.get(`/properties/${id}/reviews`);
      setReviews(r.data);
      const p = await api.get(`/properties/${id}`);
      setProperty(p.data);
      setReviewComment("");
      setReviewOpen(false);
      toast.success("Thanks for the review!");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const images = property.images && property.images.length > 0
    ? property.images
    : ["https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1600"];

  const openLightbox = (idx) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };
  const nextImage = () => setLightboxIndex((i) => (i + 1) % images.length);
  const prevImage = () => setLightboxIndex((i) => (i - 1 + images.length) % images.length);

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-10 py-8" data-testid="property-detail-page">
      <Link to="/search" className="inline-flex items-center gap-2 text-sm text-[hsl(100_3%_30%)] mb-6 hover:text-[hsl(10_51%_55%)]">
        <ArrowLeft className="h-4 w-4" /> Back to search
      </Link>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[hsl(10_51%_55%)]">{property.property_type} · {property.location}</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-2" data-testid="property-title">{property.title}</h1>
          <p className="mt-2 text-[hsl(100_3%_30%)] flex items-center gap-2 text-sm"><MapPin className="h-4 w-4" /> {property.address}</p>
        </div>
        <div className="flex items-center gap-3">
          {property.rating > 0 && (
            <div className="flex items-center gap-1.5">
              <Star className="h-4 w-4 fill-[hsl(10_51%_55%)] text-[hsl(10_51%_55%)]" />
              <span className="font-medium">{property.rating.toFixed(1)}</span>
              <span className="text-sm text-[hsl(100_3%_30%)]">· {property.review_count} reviews</span>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={toggleWishlist} data-testid="property-wishlist-btn">
            <Heart className={`h-4 w-4 mr-2 ${wishlisted ? "fill-[hsl(10_51%_55%)] text-[hsl(10_51%_55%)]" : ""}`} />
            {wishlisted ? "Saved" : "Save"}
          </Button>
        </div>
      </div>

      {/* Gallery */}
      <div className="relative grid grid-cols-4 grid-rows-2 gap-2 md:gap-3 rounded-md overflow-hidden mb-10" data-testid="gallery">
        <button
          type="button"
          onClick={() => openLightbox(0)}
          className="col-span-4 md:col-span-2 row-span-2 card-image cursor-zoom-in"
          data-testid="gallery-main"
        >
          <div className="aspect-[16/10] md:h-full overflow-hidden">
            <img src={images[0]} alt="" className="w-full h-full object-cover" />
          </div>
        </button>
        {images.slice(1, 5).map((img, i) => (
          <button
            key={i}
            type="button"
            onClick={() => openLightbox(i + 1)}
            className="hidden md:block card-image cursor-zoom-in"
            data-testid={`gallery-thumb-${i}`}
          >
            <div className="h-full overflow-hidden">
              <img src={img} alt="" className="w-full h-full object-cover" />
            </div>
          </button>
        ))}
        <button
          type="button"
          onClick={() => openLightbox(0)}
          className="absolute bottom-4 right-4 md:bottom-6 md:right-6 inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur px-4 py-2 text-sm font-medium border border-[hsl(165_37%_17%/0.15)] hover:bg-white transition-colors shadow-sm"
          data-testid="show-all-photos-btn"
        >
          <Images className="h-4 w-4" /> Show all {images.length} photos
        </button>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center"
          data-testid="lightbox"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
            className="absolute top-4 right-4 md:top-6 md:right-6 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white transition-colors"
            data-testid="lightbox-close"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="absolute top-4 left-4 md:top-6 md:left-6 text-white text-sm font-medium bg-white/10 backdrop-blur rounded-full px-3 py-1.5" data-testid="lightbox-counter">
            {lightboxIndex + 1} / {images.length}
          </div>

          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-3 md:left-6 h-11 w-11 md:h-12 md:w-12 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur flex items-center justify-center text-white transition-colors"
                data-testid="lightbox-prev"
                aria-label="Previous"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-3 md:right-6 h-11 w-11 md:h-12 md:w-12 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur flex items-center justify-center text-white transition-colors"
                data-testid="lightbox-next"
                aria-label="Next"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          <img
            src={images[lightboxIndex]}
            alt=""
            className="max-w-[92vw] max-h-[85vh] object-contain rounded"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />

          {/* Thumbnails strip */}
          {images.length > 1 && (
            <div
              className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 max-w-[90vw] overflow-x-auto no-scrollbar flex gap-2 px-2"
              onClick={(e) => e.stopPropagation()}
            >
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxIndex(i)}
                  className={`h-14 w-20 md:h-16 md:w-24 rounded overflow-hidden flex-shrink-0 border-2 transition-all ${
                    i === lightboxIndex ? "border-white opacity-100" : "border-transparent opacity-50 hover:opacity-90"
                  }`}
                  data-testid={`lightbox-thumb-${i}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
        {/* Left */}
        <div className="lg:col-span-2 space-y-10">
          <section>
            <h2 className="font-serif-display text-2xl mb-3">The home</h2>
            <p className="text-[hsl(90_4%_10%)] leading-relaxed text-[15px]">{property.description}</p>
            <div className="mt-6 flex flex-wrap gap-6 text-sm text-[hsl(100_3%_30%)]">
              <div className="flex items-center gap-2"><Bed className="h-4 w-4" /> {property.bedrooms} bedrooms</div>
              <div className="flex items-center gap-2"><Bath className="h-4 w-4" /> {property.bathrooms} bathrooms</div>
              <div className="flex items-center gap-2"><Users className="h-4 w-4" /> up to {property.max_guests} guests</div>
            </div>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl mb-4">Amenities</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {property.amenities.map((a) => (
                <div key={a} className="flex items-center gap-3 text-sm py-2" data-testid={`amenity-${a.toLowerCase().replace(/\s+/g, "-")}`}>
                  {amenityIcon(a)}
                  <span>{a}</span>
                </div>
              ))}
            </div>
          </section>

          <section data-testid="reviews-section">
            <div className="flex items-end justify-between mb-4">
              <h2 className="font-serif-display text-2xl">Reviews ({reviews.length})</h2>
              {user && user !== false && (
                <Button size="sm" variant="outline" onClick={() => setReviewOpen(!reviewOpen)} data-testid="add-review-btn">
                  {reviewOpen ? "Cancel" : "Write a review"}
                </Button>
              )}
            </div>

            {reviewOpen && (
              <div className="border border-[hsl(165_37%_17%/0.1)] rounded-md p-4 mb-6 bg-white" data-testid="review-form">
                <div className="flex items-center gap-2 mb-3">
                  {[1,2,3,4,5].map((n) => (
                    <button key={n} onClick={() => setReviewRating(n)} data-testid={`star-${n}`}>
                      <Star className={`h-5 w-5 ${n <= reviewRating ? "fill-[hsl(10_51%_55%)] text-[hsl(10_51%_55%)]" : "text-[hsl(100_3%_30%)]"}`} />
                    </button>
                  ))}
                </div>
                <Textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder="Tell us about your stay..." rows={4} data-testid="review-comment" />
                <div className="mt-3 flex justify-end">
                  <Button onClick={submitReview} className="btn-terracotta rounded-full" data-testid="submit-review">Post review</Button>
                </div>
              </div>
            )}

            {reviews.length === 0 ? (
              <p className="text-sm text-[hsl(100_3%_30%)]">No reviews yet. Be the first to share.</p>
            ) : (
              <div className="space-y-6">
                {reviews.map((r) => (
                  <div key={r.id} className="border-b border-[hsl(165_37%_17%/0.08)] pb-6" data-testid={`review-${r.id}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-9 w-9 rounded-full bg-[hsl(42_22%_88%)] flex items-center justify-center font-medium">
                        {r.user_name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{r.user_name}</p>
                        <div className="flex items-center gap-1">
                          {[1,2,3,4,5].map((n) => (
                            <Star key={n} className={`h-3 w-3 ${n <= r.rating ? "fill-[hsl(10_51%_55%)] text-[hsl(10_51%_55%)]" : "text-[hsl(100_3%_30%)]"}`} />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-[hsl(90_4%_10%)] leading-relaxed">{r.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Right — booking widget */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-28 border border-[hsl(165_37%_17%/0.12)] rounded-md p-6 bg-white shadow-[0_10px_30px_rgba(0,0,0,0.04)]" data-testid="booking-widget">
            <div className="flex items-baseline gap-2 mb-4">
              <span className="font-serif-display text-3xl">₹{property.price_per_night.toLocaleString("en-IN")}</span>
              <span className="text-sm text-[hsl(100_3%_30%)]">/ night</span>
            </div>

            <div className="grid grid-cols-2 border border-[hsl(165_37%_17%/0.12)] rounded-md overflow-hidden">
              <Popover>
                <PopoverTrigger asChild>
                  <button className="p-3 text-left border-r border-[hsl(165_37%_17%/0.12)]" data-testid="widget-checkin">
                    <div className="text-[10px] uppercase tracking-widest text-[hsl(100_3%_30%)]">Check in</div>
                    <div className="text-sm mt-1 flex items-center gap-1.5">
                      <CalendarIcon className="h-3.5 w-3.5" />
                      {checkIn ? format(checkIn, "MMM d") : "Select"}
                    </div>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 bg-white">
                  <Calendar mode="single" selected={checkIn} onSelect={setCheckIn} disabled={{ before: new Date() }} />
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="p-3 text-left" data-testid="widget-checkout">
                    <div className="text-[10px] uppercase tracking-widest text-[hsl(100_3%_30%)]">Check out</div>
                    <div className="text-sm mt-1 flex items-center gap-1.5">
                      <CalendarIcon className="h-3.5 w-3.5" />
                      {checkOut ? format(checkOut, "MMM d") : "Select"}
                    </div>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 bg-white">
                  <Calendar mode="single" selected={checkOut} onSelect={setCheckOut} disabled={{ before: checkIn || new Date() }} />
                </PopoverContent>
              </Popover>
              <div className="col-span-2 border-t border-[hsl(165_37%_17%/0.12)] p-3">
                <div className="text-[10px] uppercase tracking-widest text-[hsl(100_3%_30%)]">Guests</div>
                <Input
                  type="number"
                  min={1}
                  max={property.max_guests}
                  value={guests}
                  onChange={(e) => setGuests(Number(e.target.value))}
                  className="border-0 h-8 mt-1 px-0 focus-visible:ring-0"
                  data-testid="widget-guests"
                />
              </div>
            </div>

            {nights > 0 && (
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>₹{property.price_per_night.toLocaleString("en-IN")} × {nights} nights</span>
                  <span>₹{subtotal.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between text-[hsl(100_3%_30%)]">
                  <span>Taxes & fees (12%)</span>
                  <span>₹{taxes.toLocaleString("en-IN")}</span>
                </div>
                <div className="pt-3 border-t border-[hsl(165_37%_17%/0.12)] flex justify-between font-medium">
                  <span>Total</span>
                  <span>₹{total.toLocaleString("en-IN")}</span>
                </div>
              </div>
            )}

            <Button onClick={onReserve} className="w-full btn-terracotta rounded-full h-12 mt-5" data-testid="reserve-btn">
              Reserve
            </Button>
            <a
              href={whatsappUrl({
                propertyTitle: property.title,
                address: property.address,
                checkIn: checkIn ? format(checkIn, "yyyy-MM-dd") : undefined,
                checkOut: checkOut ? format(checkOut, "yyyy-MM-dd") : undefined,
                guests,
              })}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full h-12 border border-[#25D366]/40 text-[#128C3D] hover:bg-[#25D366]/10 transition-colors text-sm font-medium"
              data-testid="whatsapp-widget-btn"
            >
              <WhatsAppIcon className="h-4 w-4" /> Enquire on WhatsApp
            </a>
            <p className="text-xs text-center text-[hsl(100_3%_30%)] mt-3">You won&apos;t be charged yet</p>
          </div>
        </aside>
      </div>

      {/* Mobile floating WhatsApp FAB */}
      <a
        href={whatsappUrl({
          propertyTitle: property.title,
          address: property.address,
          checkIn: checkIn ? format(checkIn, "yyyy-MM-dd") : undefined,
          checkOut: checkOut ? format(checkOut, "yyyy-MM-dd") : undefined,
          guests,
        })}
        target="_blank"
        rel="noopener noreferrer"
        className="lg:hidden fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-[#25D366] shadow-[0_10px_30px_rgba(37,211,102,0.35)] flex items-center justify-center text-white hover:bg-[#1fb95a] transition-colors"
        data-testid="whatsapp-fab"
        aria-label="Chat on WhatsApp"
      >
        <WhatsAppIcon className="h-6 w-6" />
      </a>
    </div>
  );
}
